import type { PageComponentMetaWithPosition } from '@/core/features/a-dynamic-page/dynamic-page-pacts/dynamic-page-type';
import type { PageComponentProps } from '@/features/a-dynamic-page/components/page-component-switch';
import type { ApplyTravelFormFilter } from '@/features/travel-form/travel-form-desktop/travel-form-desktop';
import type { TravelFormOverlayName } from '@/features/travel-form/travel-form-expanded/travel-form-expanded';
import type { TravelFormV1Attributes } from '@/features/travel-form/travel-form-v1/travel-form-v1-type';

import React from 'react';

import { useDynamicPageQueryFetching } from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-query';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import { useScrollToTarget } from '@/core/features/link/use-scroll-to-target';
import { useRouterLink } from '@/core/features/router/router-link';
import useTravelFormDataV1 from '@/features/travel-form/travel-form-data-v1/use-travel-form-data-v1';
import TravelFormV1 from '@/features/travel-form/travel-form-v1/travel-form-v1';
import TravelFormV1Desktop from '@/features/travel-form/travel-form-v1/travel-form-v1-desktop/travel-form-v1-desktop';
import { trackTravelFormCTAClick } from '@/features/travel-form/use-travel-form-tracking';

type TravelFormV1ContainerProps = PageComponentProps<TravelFormV1Attributes, PageComponentMetaWithPosition>;

export default function TravelFormV1Container({ attributes, isWidget, meta }: TravelFormV1ContainerProps) {
    const { isNavigating } = useRouterLink();
    const { isFetching: isDynamicPageFetching } = useDynamicPageQueryFetching();
    const { banner, travelForm: partialTravelForm } = attributes;
    const [overlayType, setOverlayType] = React.useState<TravelFormOverlayName | null>(null);

    const { isDesktopView } = useDeviceoutput();

    const {
        activeFilters,
        applyFilter,
        initialCtaLinkUrl,
        isLoading,
        requestTargets,
        scrollTo,
        submitForm,
        travelForm: travelFormData,
    } = useTravelFormDataV1({ handlePathReset: true });
    const { scrollTo: scrollToComponent } = useScrollToTarget(scrollTo?.scrollToTarget ?? null);

    const [hasSubmitted, setHasSubmitted] = React.useState(false);

    React.useEffect(() => {
        if (hasSubmitted && !isNavigating) {
            setTimeout(() => {
                scrollToComponent();
                setHasSubmitted(false);
            }, 400);
        }
    }, [hasSubmitted, isNavigating, scrollToComponent, isDynamicPageFetching]);

    const fields = travelFormData?.filters ?? partialTravelForm.fields;

    const onOverlayCtaClick = (config: ApplyTravelFormFilter) => {
        applyFilter(config);
        setOverlayType(null);
    };

    const onSubmit = () => {
        const linkUrl = travelFormData?.cta.linkUrl ?? initialCtaLinkUrl ?? partialTravelForm.cta.linkUrl;
        const activityFilters = travelFormData?.filters.activity;

        const selectedActivitiesNames = activityFilters?.selectedItems
            .sort((activity1, activity2) => activity1.id - activity2.id)
            .map((selectedActivity) => selectedActivity.title);

        setHasSubmitted(true);
        trackTravelFormCTAClick(selectedActivitiesNames ?? []);
        submitForm(linkUrl);
    };

    //TODO: Can be removed after backend releases INDI-8422
    type TravelFormFields = TravelFormV1Attributes['travelForm']['fields'];
    const fieldsWithDisplayFallback = React.useMemo(() => {
        return Object.entries(fields).reduce((acc, [key, value]) => {
            const newField = value ? { ...value, display: value.display ?? true } : null;

            // eslint-disable-next-line fp/no-mutation
            acc[key as keyof TravelFormFields] = newField;
            return acc;
        }, {} as TravelFormFields);
    }, [fields]);

    const formConfiguration = {
        activeFilters,
        cta: {
            dependsOn: travelFormData?.cta.dependsOn ?? partialTravelForm.cta.dependsOn,
            display: travelFormData?.cta.display ?? true,
            text: travelFormData?.cta.text ?? partialTravelForm.cta.text,
        },
        fields: fieldsWithDisplayFallback,
        isLoading,
        noPadding: true,
        onSubmit,
        openOverlay: setOverlayType,
    };

    if (isDesktopView) {
        return (
            <TravelFormV1Desktop
                activityRequestTarget={requestTargets.activityRequestTarget}
                airportRequestTarget={requestTargets.airportsRequestTarget}
                banner={banner}
                dateRequestTarget={requestTargets.dateRequestTarget}
                destinationChangeRequestTarget={requestTargets.destinationChangeRequestTarget}
                destinationRequestTarget={requestTargets.destinationRequestTarget}
                formConfiguration={{
                    ...formConfiguration,
                    inputSize: partialTravelForm.orientation === 'vertical' ? 'normal' : 'large',
                    orientation: partialTravelForm.orientation,
                }}
                isSticky={meta.position === 'sticky'}
                isWidget={isWidget ?? false}
                onOverlayClose={() => setOverlayType(null)}
                onOverlayCtaClick={onOverlayCtaClick}
                openOverlay={setOverlayType}
                overlayType={overlayType}
                travelForm={travelFormData}
            />
        );
    }

    return (
        <TravelFormV1
            activityRequestTarget={requestTargets.activityRequestTarget}
            airportsRequestTarget={requestTargets.airportsRequestTarget}
            banner={banner}
            dateRequestTarget={requestTargets.dateRequestTarget}
            destinationChangeRequestTarget={requestTargets.destinationChangeRequestTarget}
            destinationRequestTarget={requestTargets.destinationRequestTarget}
            formConfiguration={formConfiguration}
            onOverlayClose={() => setOverlayType(null)}
            onOverlayCtaClick={onOverlayCtaClick}
            overlayType={overlayType}
            travelForm={travelFormData}
        />
    );
}
